/**
* AbstractDialogPlayerMediators.js 
* 
* Created on 3-okt-2013, 15:35:41
*
* @author Ivo Swartjes for Ranj
**/

import ContentTypes from "/lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import { isNull } from "/lib/html5-software-library/src/ranj/util/TypeUtil";
import NGTDialogPlayerEvent from "../NGTDialogPlayerEvent";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";

export class AbstractDialogPlayerMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager
     * @param {ClickEaterFactory} clickEaterFactory
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._onDialogContentBound = this._onDialogContent.bind(this);
        this._onDialogOptionsChangedBound = this._onDialogOptionsChanged.bind(this);
        this._onDialogOptionChosenBound = this._onDialogOptionChosen.bind(this);
        this._onCommandSequenceEndBound = this._onCommandSequenceEnd.bind(this);
        this._onDialogEndBound = this._onDialogEnd.bind(this);
        this._currentCharacterID;
    }

    destroy() {
        super.destroy();

        this._removeDialogPlayerListeners();
    }

    // Override these
    _handleTextContent() { }

    _handleAudioContent() { }
    _handleImageContent() { }
    _handleDialogOptionsChanged() { }
    _handleDialogOptionChosen() { }
    _handleOverlayImageContent() { }
    _handleMovieContent() { }
    _handleCommandSequenceEnd() { }

    // ------------------------------------------
    // 			Dialog listeners
    // ------------------------------------------

    _addDialogPlayerListeners() {
        if (isNull(this._dialogPlayer)) return;

        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, this._onDialogOptionsChangedBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, this._onDialogOptionChosenBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._onCommandSequenceEndBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_END, this._onDialogEndBound);
    }

    _removeDialogPlayerListeners() {
        if (isNull(this._dialogPlayer)) return;

        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, this._onDialogOptionsChangedBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, this._onDialogOptionChosenBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._onCommandSequenceEndBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_END, this._onDialogEndBound);
    }

    // ------------------------------------------
    // 			Dialog handlers
    // ------------------------------------------

    _onDialogContent(evt) {
        if (evt.params.contentPlayer !== this.getDialogPlayer()) {
            return;
        }

        const data = evt.params.data;
        const dialogContentPackage = data.data;
        if (dialogContentPackage !== null) {
            this._currentCharacterID = dialogContentPackage.characterID;
            const content = dialogContentPackage.content;
            switch (content.getType()) {
                case ContentTypes.TYPE_TEXT:
                    this._handleTextContent(content);
                    this._dialogPlayer.contentCompleted(content);
                    break;
                case ContentTypes.TYPE_AUDIO:
                    this._handleAudioContent(content);
                    break;
                case ContentTypes.TYPE_IMAGE:
                    this._handleImageContent(content);
                    this._dialogPlayer.contentCompleted(content);
                    break;
                case ContentTypes.TYPE_OVERLAY_IMAGE:
                    this._handleOverlayImageContent(content);
                    this._dialogPlayer.contentCompleted(content);
                    break;
                case ContentTypes.TYPE_MOVIE:
                    this._handleMovieContent(content);
                    break;

            }
        }
    }

    _onDialogOptionsChanged(evt) {
        if (evt.params.contentPlayer !== this.getDialogPlayer()) {
            return;
        }

        this._handleDialogOptionsChanged();
    }

    _onDialogOptionChosen(evt) {
        if (evt.params.contentPlayer !== this.getDialogPlayer()) {
            return;
        }

        const data = evt.params.data;
        this._handleDialogOptionChosen(data.option);
    }

    _onDialogEnd(evt) {
        if (evt.params.contentPlayer !== this.getDialogPlayer()) {
            return;
        }

        this.setDialogPlayer(null);
    }
    
    _onCommandSequenceEnd(evt) {
        if (evt.params.contentPlayer !== this.getDialogPlayer()) {
            return;
        }

        this._handleCommandSequenceEnd();
    }

    // ------------------------------------------
    // 			Content delegates
    // ------------------------------------------

    getDialogPlayer() {
        return this._dialogPlayer;
    }

    setDialogPlayer(value) {
        if (this._dialogPlayer !== null) {
            this._removeDialogPlayerListeners();
        }

        this._dialogPlayer = value;

        if (this._dialogPlayer !== null) {
            this._addDialogPlayerListeners();
        }
    }

    _handleDialogOptionChosen() { }
}